import GetAppIcon from '@mui/icons-material/GetApp';
import { Button, Theme, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridCellValue,
  GridSortModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { reformatCamelCaseString } from '../../../common/helperFunctions/formatter';
import { Role } from '../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../../modules/Manage/pages/queues-table/DataTable';
import {
  refreshDurations,
  ToolbarRefreshButton,
} from '../../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { ModalWithStyles } from './ModalWithStyles';

const useStyles = makeStyles((theme: Theme) => ({
  cancelButton: {
    marginLeft: theme.spacing(1), // space between fields
    float: 'right',
  },
  refreshButton: {
    float: 'left',
  },
  footer: {
    width: '100%',
    display: 'inline-block',
  },
}));

export const SpreadsheetsModal = ({
  showSpreadsheets,
  handleHideSpreadsheets,
  id,
}) => {
  const classes = useStyles({});
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents as IPermissions;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  const [powerlistCached, powerlistLoadable] = makeApiRequestCached<any>(
    {
      endpoint: `${endpointEnum.GET_POWERLIST}/${id}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data.powerlist ?? cached.current
  );

  const setRefetchToggleForPowerlistSpreadsheets = useSetRecoilState(
    refetchToggle(`${endpointEnum.GET_POWERLIST}/${id}`)
  );

  const spreadsheetRows = useMemo(
    () =>
      powerlistCached?.current?.powerlistSpreadsheets?.map((sheet: any) => ({
        ...sheet,
        id: sheet.powerlistSpreadsheetId,
      })),
    [powerlistLoadable]
  );

  const [GridsortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'uploadDate', sort: 'desc' },
  ]);
  const enumFormatter = (params: GridValueFormatterParams): GridCellValue => {
    return reformatCamelCaseString(params.value as string);
  };

  const [businessDataCached] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current,
    {} as any
  );
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      headerClassName: 'superStyle',
      hide: !isSuperAdminBoolean,
      disableExport: !isSuperAdminBoolean,
    },
    {
      field: 'name',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'status',
      headerName: 'Status',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: enumFormatter,
    },
    {
      field: 'uploadedByUserName',
      headerName: 'Uploaded By',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'duplicateHandling',
      headerName: 'Duplicate Handling',
      // headerClassName: dataTableScss.fullWidth,
      // cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'uploadDate',
      headerName: 'Upload Date',
      width: 165,
      valueFormatter: (params) =>
        params.row.uploadDate
          ? DateTime.fromSQL(params.row.uploadDate, { zone: 'UTC' })
              .setZone(businessDataCached.current?.timezone_php)
              .toLocaleString(DateTime.DATETIME_SHORT)
          : '-',
    },
    {
      field: 'validCount',
      headerName: 'Valid Count',
      cellClassName: dataTableScss.center,
      // width: 105,
    },
    {
      field: 'errorCount',
      headerName: 'Error Count',
      cellClassName: dataTableScss.center,
      align: 'center',
      // width: 105,
    },
    {
      field: 'warningCount',
      headerName: 'Warning Count',
      cellClassName: dataTableScss.center,
      align: 'center',
      // width: 105,
    },
    {
      field: '8',
      headerName: 'Action',
      cellClassName: dataTableScss.center,
      headerAlign: 'center',
      width: 105,
      renderCell: (params) => {
        if (params.row.errorFileS3Key) {
          return (
            <Tooltip title="Download Error Spreadsheet">
              <IconButton
                href={`${process.env.VITE_APP_POWERLIST_BUCKET}/${params.row.errorFileS3Key}`}
                download
                size="large"
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
          );
        } else return <></>;
      },
    },
  ] /* satisfies */ as IColDef[];

  return (
    <ModalWithStyles
      title="Upload Status"
      size="lg"
      show={showSpreadsheets}
      onHide={handleHideSpreadsheets}
      submitLabel=""
      cancelLabel="Close"
      footerComponent={
        <div className={classes.footer}>
          <div className={classes.refreshButton}>
            <ToolbarRefreshButton
              id="PowerlistSpreadsheets"
              duration={refreshDurations.dur30sec}
              onClickForRefresh={() => {
                setRefetchToggleForPowerlistSpreadsheets({});
              }}
            />
          </div>
          <div>
            <Button
              className={classes.cancelButton}
              variant="contained"
              disableElevation={true}
              onClick={handleHideSpreadsheets}
            >
              Close
            </Button>
          </div>
        </div>
      }
    >
      <DataTable
        // rows={powerlistCached.current?.powerlistSpreadsheets ?? []}
        rows={spreadsheetRows ?? []}
        loading={powerlistLoadable?.state !== 'hasValue'}
        columns={columns}
        sortModel={GridsortModel}
        setGridSortModel={setGridSortModel}
      />
    </ModalWithStyles>
  );
};
